import {
  useSelect,
  UseSelectState,
  UseSelectStateChangeOptions,
  UseSelectStateChange,
  UseSelectReturnValue,
} from 'downshift'
import { uniqueId } from 'lodash'
import {
  Context,
  createContext,
  Provider,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { type MenuOption } from '@opallabs/concepts'
import { useOnce } from '@opallabs/lambda'
import { useRadixDropdownBlurHelper } from '../../../utils/RadixDropdownBlurHelper'
import { BodyProps, UnwrappedValue } from '../types'
import { useSingleSelectData } from './DataContext'
import { useSingleSelectSearch } from './SearchContext'

type AllowedDownshiftProps =
  | 'isOpen'
  | 'getToggleButtonProps'
  | 'getLabelProps'
  | 'getMenuProps'
  | 'highlightedIndex'
  | 'setHighlightedIndex'
  | 'getItemProps'
  | 'closeMenu'
  | 'selectItem'
  | 'selectedItem'

type AdditionalValues<T> = {
  componentId: string
  filteredOptions: Array<T>
  hasFooter: boolean
  hasLabel: boolean
  hideFooterOnSearch?: boolean
  isInvalidSelection: boolean
  menuContainerRef: RefObject<HTMLDivElement>
  onBlur?: () => void
  optionCount: number
  selectItem: unknown
  setHasFooter: (footerRendered: boolean) => void
  setHasLabel: (labelRendered: boolean) => void
  setMenuContainerRef: (ref: RefObject<HTMLDivElement>) => void
  toggleRef: RefObject<HTMLButtonElement>
}

type ContextValue<
  Option extends MenuOption<OptionValue>,
  OptionValue extends string | number | boolean = UnwrappedValue<Option>,
> = Pick<UseSelectReturnValue<Option>, AllowedDownshiftProps> &
  AdditionalValues<Option>

const StateContext = createContext<
  ContextValue<MenuOption, string | number | boolean> | undefined
>(undefined)
StateContext.displayName = 'Lapidary: StateContext ← SingleSelect'

export const StateProvider = <
  Option extends MenuOption<OptionValue>,
  OptionValue extends string | number | boolean = UnwrappedValue<Option>,
>({
  children,
  clearInvalidSelection,
  externalRef,
  hideFooterOnSearch,
  initialIsOpen,
  isClearable,
  onBlur,
  onOpenChange,
  shouldStayOpenOnSelect = false,
}: BodyProps) => {
  const [menuContainerRef, setMenuContainerRef] = useState<
    RefObject<HTMLDivElement>
  >({ current: null })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const toggleRef = externalRef ?? buttonRef

  const [hasFooter, setHasFooter] = useState(false)
  const [hasLabel, setHasLabel] = useState(false)

  const { initialSelection, options, onChange, selectedOption } =
    useSingleSelectData<Option, OptionValue>()

  const {
    focusSearch,
    isSearchable,
    searchFocused,
    searchTerm,
    setSearchTerm,
  } = useSingleSelectSearch()

  const [componentId] = useState(uniqueId('lapidary-single-select-'))

  const stateReducer = useCallback(
    (
      state: UseSelectState<Option>,
      actionAndChanges: UseSelectStateChangeOptions<Option>
    ): Partial<UseSelectState<Option>> => {
      const { changes, type } = actionAndChanges
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownArrowUp:
          return {
            ...changes,
            highlightedIndex:
              // Force state change if pressing Up on first item, so that
              // the `onStateChange` handler can focus the search input
              isSearchable &&
              state.highlightedIndex === 0 &&
              changes.highlightedIndex === 0
                ? -1
                : changes.highlightedIndex,
          }
        case useSelect.stateChangeTypes.ToggleButtonBlur:
          return {
            ...changes,
            // keep the menu open if focusing search
            isOpen: true,
            highlightedIndex: searchFocused ? -1 : changes.highlightedIndex,
            selectedItem: hasFooter ? null : changes.selectedItem,
          }
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          if (
            isClearable &&
            changes.selectedItem?.value === state.selectedItem?.value
          ) {
            return {
              ...changes,
              selectedItem: null,
            }
          }
          if (shouldStayOpenOnSelect) {
            return {
              ...changes,
              highlightedIndex: state.highlightedIndex,
              isOpen: true,
            }
          }
          break
      }
      return changes
    },
    [
      isSearchable,
      searchFocused,
      isClearable,
      hasFooter,
      shouldStayOpenOnSelect,
    ]
  )

  const onStateChange = useCallback(
    ({ type, highlightedIndex }: UseSelectStateChange<Option>) => {
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownArrowUp:
          // If pressing Up on first menu option focus the search input
          // focusSearch is only defined when SearchableRoot is used
          if (highlightedIndex === -1) {
            focusSearch?.()
          }
      }
    },
    [focusSearch]
  )

  const onIsOpenChange = useCallback(
    ({ isOpen = false }: UseSelectStateChange<Option>) => {
      onOpenChange?.(isOpen)
      if (isOpen) {
        focusSearch?.()
      } else {
        if (searchTerm !== '') setSearchTerm('')

        // Make sure toggle button re-focuses if escape is pressed
        // while the search input is focused
        toggleRef.current?.focus()
      }
    },
    [onOpenChange, focusSearch, searchTerm, setSearchTerm, toggleRef]
  )

  // called on item click and enter keypress (from state reducer)
  const onSelectedItemChange = ({
    selectedItem: newSelectedItem,
  }: UseSelectStateChange<Option>) => {
    onChange?.(newSelectedItem ?? null)
    // selectedItemRef.current = newSelectedItem ?? null
  }

  const filteredOptions = useMemo(() => {
    if (!searchTerm) {
      return options
    }

    const searchFilteredOptions = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
    return searchFilteredOptions
  }, [options, searchTerm])

  const {
    closeMenu,
    getItemProps,
    getLabelProps,
    getMenuProps,
    getToggleButtonProps,
    highlightedIndex,
    isOpen,
    selectedItem,
    selectItem,
    setHighlightedIndex,
  } = useSelect<Option>({
    id: componentId,
    initialIsOpen,
    initialSelectedItem: initialSelection,
    isItemDisabled: (item) => !!item.disabled,
    items: filteredOptions,
    itemToString: (item) => (item ? item.label : ''),
    onIsOpenChange,
    onSelectedItemChange,
    onStateChange,
    selectedItem: selectedOption,
    stateReducer,
  })

  const isInvalidSelection = !options.find(
    (o) => o.value === selectedItem?.value
  )

  useOnce(() => {
    if (initialIsOpen && isSearchable) focusSearch?.()
  })

  useEffect(() => {
    if (clearInvalidSelection && isInvalidSelection) {
      selectItem(null)
    }
  }, [clearInvalidSelection, isInvalidSelection, selectItem])

  // Workaround to fix outside click interactions with OptionCell dropdown
  useRadixDropdownBlurHelper(isOpen, [menuContainerRef, toggleRef], () => {
    onBlur?.()
    closeMenu()
  })

  const Provider = StateContext.Provider as unknown as Provider<
    ContextValue<Option, OptionValue>
  >

  return (
    <Provider
      value={{
        closeMenu,
        componentId,
        getItemProps,
        getLabelProps,
        getMenuProps,
        getToggleButtonProps,
        filteredOptions,
        hasFooter,
        hasLabel,
        hideFooterOnSearch,
        highlightedIndex,
        isInvalidSelection,
        isOpen,
        menuContainerRef,
        onBlur,
        optionCount: filteredOptions.length,
        selectedItem,
        selectItem,
        setHasFooter,
        setHasLabel,
        setHighlightedIndex,
        setMenuContainerRef,
        toggleRef,
      }}
    >
      {children}
    </Provider>
  )
}
StateProvider.displayName = 'Lapidary: StateProvider ← SingleSelect'

export const useSingleSelectState = <
  Option extends MenuOption<OptionValue>,
  OptionValue extends string | number | boolean = UnwrappedValue<Option>,
>() => {
  const context = useContext<ContextValue<Option, OptionValue>>(
    StateContext as unknown as Context<ContextValue<Option, OptionValue>>
  )

  if (!context) {
    throw new Error(
      'useSingleSelectState must be used within SingleSelectContext.'
    )
  }

  return context
}
